import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowRight,
  Check,
  Zap,
  MessageSquare,
  ShoppingBag,
  Clock,
  Headphones,
  ChevronRight,
  CheckCircle2,
  Mail,
  CalendarCheck,
  Calendar,
  Briefcase,
  Mic,
  TrendingUp,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { ViewTab } from '../types';
import { PublicHeader } from '../components/layout/PublicHeader';
import { HeroPhoneShowcase } from '../components/landing/HeroPhoneShowcase';
import { WhatsAppIcon } from '../components/ui/WhatsAppIcon';
import { siteAnalytics } from '../services/siteAnalytics';
import { adPixels } from '../services/adPixels';
import { BLOG_POSTS } from '../data/blogPosts';

interface LandingViewProps {
  onNavigate: (tab: ViewTab) => void;
}

// Canvas interactif : Dégradé fluide en mouvement zéro-gravité & dispersion/évitement au passage du curseur
const HeroGravityCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    // Sur mobile / écran tactile, éviter la boucle canvas 60fps coûteuse en batterie/CPU
    if (window.innerWidth < 768 || window.matchMedia?.('(pointer: coarse)').matches) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = (canvas.width = canvas.parentElement?.clientWidth || window.innerWidth);
    let height = (canvas.height = canvas.parentElement?.clientHeight || 650);

    const handleResize = () => {
      if (!canvas || !canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.clientWidth;
      height = canvas.height = canvas.parentElement.clientHeight;
    };
    window.addEventListener('resize', handleResize);

    const handleMouseMove = (e: MouseEvent) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    // 3 Grands orbes de dégradé en lévitation gravitationnelle (ambiance douce chaleureuse style Together.ai / Warm Tech)
    const orbs = [
      {
        x: width * 0.28,
        y: height * 0.22,
        baseX: width * 0.28,
        baseY: height * 0.22,
        radius: Math.min(width * 0.38, 340),
        color: 'rgba(245, 158, 11, 0.06)', // Ambre chaleureux très doux
        phaseX: 0,
        phaseY: 1,
        speedX: 0.007,
        speedY: 0.005,
      },
      {
        x: width * 0.72,
        y: height * 0.28,
        baseX: width * 0.72,
        baseY: height * 0.28,
        radius: Math.min(width * 0.35, 300),
        color: 'rgba(217, 119, 6, 0.04)', // Terracotta subtile
        phaseX: 2.5,
        phaseY: 3.2,
        speedX: 0.006,
        speedY: 0.008,
      },
      {
        x: width * 0.5,
        y: height * 0.14,
        baseX: width * 0.5,
        baseY: height * 0.14,
        radius: Math.min(width * 0.46, 420),
        color: 'rgba(148, 163, 184, 0.06)', // Brume neutre douce
        phaseX: 4.2,
        phaseY: 2.1,
        speedX: 0.005,
        speedY: 0.006,
      },
    ];

    // Particules & micro-éclats en apesanteur
    const particlesCount = Math.min(Math.floor(width / 32), 48);
    const particles = Array.from({ length: particlesCount }, () => {
      const x = Math.random() * width;
      const y = Math.random() * height;
      return {
        x,
        y,
        baseX: x,
        baseY: y,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        radius: 1.5 + Math.random() * 2,
        color: Math.random() > 0.5 ? '#f59e0b' : '#94a3b8',
        alpha: 0.15 + Math.random() * 0.25,
        phase: Math.random() * Math.PI * 2,
        speed: 0.015 + Math.random() * 0.02,
      };
    });

    const render = () => {
      ctx.clearRect(0, 0, width, height);
      const mouse = mouseRef.current;

      // 1. Mise à jour & tracé des orbes avec répulsion gravitationnelle
      orbs.forEach((orb) => {
        orb.phaseX += orb.speedX;
        orb.phaseY += orb.speedY;
        orb.baseX += Math.sin(orb.phaseX) * 0.65;
        orb.baseY += Math.cos(orb.phaseY) * 0.45;

        // Dispersion au passage du curseur ("évite et se disperse pour donner de l'espace")
        const dx = orb.x - mouse.x;
        const dy = orb.y - mouse.y;
        const dist = Math.hypot(dx, dy);
        const repelRadius = 260;
        if (dist < repelRadius && dist > 0) {
          const force = (repelRadius - dist) / repelRadius;
          const angle = Math.atan2(dy, dx);
          orb.x += Math.cos(angle) * force * 11;
          orb.y += Math.sin(angle) * force * 11;
        }

        // Retour fluide à l'orbite d'origine
        orb.x += (orb.baseX - orb.x) * 0.035;
        orb.y += (orb.baseY - orb.y) * 0.035;

        const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
        grad.addColorStop(0, orb.color);
        grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // 2. Mise à jour & tracé des micro-particules avec répulsion vive
      particles.forEach((p) => {
        p.phase += p.speed;
        p.baseX += p.vx;
        p.baseY += p.vy;

        if (p.baseX < 0) p.baseX = width;
        if (p.baseX > width) p.baseX = 0;
        if (p.baseY < 0) p.baseY = height;
        if (p.baseY > height) p.baseY = 0;

        // Dispersion au curseur
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const dist = Math.hypot(dx, dy);
        const repelRadius = 160;
        if (dist < repelRadius && dist > 0) {
          const force = (repelRadius - dist) / repelRadius;
          const angle = Math.atan2(dy, dx);
          p.x += Math.cos(angle) * force * 10;
          p.y += Math.sin(angle) * force * 10;
        }

        p.x += (p.baseX - p.x) * 0.07;
        p.y += (p.baseY - p.y) * 0.07;

        ctx.save();
        ctx.globalAlpha = p.alpha * (0.6 + 0.4 * Math.sin(p.phase));
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />;
};

export const LandingView: React.FC<LandingViewProps> = ({ onNavigate }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  useEffect(() => {
    siteAnalytics.setupSectionObserver();
    adPixels.track('ViewContent', { content_name: 'landing' });
  }, []);

  const faqs = [
    {
      q: "Mon téléphone doit-il rester allumé et connecté à Internet ?",
      a: "Non, absolument pas. Zeyilo fonctionne 24h/24 et 7j/7 directement dans le cloud sur des serveurs haute disponibilité. Même si votre smartphone est éteint, déchargé, sans connexion ou en mode avion, votre assistant continue d'accueillir vos prospects, répondre aux demandes et enregistrer vos commandes sans interruption.",
    },
    {
      q: "Est-ce sécurisé pour mon numéro WhatsApp existant ?",
      a: "Absolument. Zeyilo est conçu pour un usage professionnel rigoureux : l'assistant répond uniquement aux sollicitations directes de vos interlocuteurs, sans aucun démarchage intrusif ni émission de masse non sollicitée. L'architecture Zero-Cache assure la persistance de votre session en base chiffrée (aucun besoin de rescannage continu), et le cadencement des échanges respecte les seuils de sécurité de Meta. Pour les structures nécessitant une intégration d'entreprise certifiée, Zeyilo supporte également la connexion native à l'API Cloud officielle de Meta (WhatsApp Business Platform).",
    },
    {
      q: "Comment l'assistant apprend-il mes produits, services et tarifs ?",
      a: "En moins de 5 minutes. Il vous suffit d'importer votre catalogue de produits, d'ajouter vos fiches PDF (menu, catalogue, grille de tarifs, conditions) ou simplement de saisir vos consignes dans votre espace de gestion. Zeyilo assimile instantanément vos informations et applique rigoureusement vos règles commerciales.",
    },
    {
      q: "Puis-je reprendre la main sur une conversation à tout moment ?",
      a: "À tout instant et en toute simplicité. Dès que vous écrivez vous-même dans la discussion WhatsApp, l'assistant se met immédiatement en pause pour vous laisser échanger directement avec votre interlocuteur. Vous gardez le contrôle total en permanence et pouvez réactiver l'assistant quand vous le souhaitez.",
    },
    {
      q: "Comment fonctionne la compréhension des messages vocaux ?",
      a: "Notre intelligence artificielle écoute et retranscrit les messages audio de vos clients en quelques secondes (en français ou anglais), même avec du bruit ambiant ou un débit rapide. Elle comprend parfaitement les demandes et expressions courantes, puis répond par écrit avec clarté, naturel et précision.",
    },
    {
      q: "Comment les clients règlent-ils leurs commandes et paiements ?",
      a: "Zeyilo n'intermédie aucun flux financier et prélève 0 % de commission. Dès qu'un client passe commande ou réserve, l'assistant transmet vos coordonnées Mobile Money officielles (Wave, Orange Money, MTN, Moov) ou vos liens de paiement directs. Dès que le client envoie une capture d'écran de reçu, l'IA analyse l'image, en extrait les données clés (ID de transaction, montant, date, expéditeur) et vérifie la concordance avec le montant total. Vous gardez la validation finale avant expédition, garantissant une intégrité totale sans risque de faux reçu validé par erreur.",
    },
    {
      q: "Puis-je gérer plusieurs entreprises clientes ou créer une offre d'agence avec Zeyilo ?",
      a: "Oui. Zeyilo intègre une architecture multi-comptes adaptée aux agences et prestataires de services via le forfait Agence (3 numéros WhatsApp inclus dès le départ, extensible par modules de comptes supplémentaires). Chaque numéro connecté dispose d'un cloisonnement étanche (catalogue, base documentaire RAG, horaires et accès collaborateurs propres). L'option Marque Blanche supprime toute mention de Zeyilo dans les échanges sortants, et des webhooks sont disponibles pour synchroniser les données avec les CRM ou ERP tiers.",
    },
    {
      q: "L'assistant peut-il gérer la prise de rendez-vous et les réservations ?",
      a: "Oui. Vous configurez vos horaires d'ouverture et la durée de vos prestations. Zeyilo consulte vos disponibilités en temps réel, propose les créneaux libres, enregistre le rendez-vous et transmet une confirmation automatique avec rappel au client sur WhatsApp.",
    },
    {
      q: "Que se passe-t-il si un client pose une question imprévue ou complexe ?",
      a: "L'assistant n'invente jamais de réponse et ne fait aucune fausse promesse. S'il ne dispose pas de l'information dans vos documents, il invite courtoisement le client à patienter et vous transfère immédiatement la demande avec le récapitulatif de l'échange.",
    },
    {
      q: "Puis-je personnaliser le ton et le style de réponse ?",
      a: "Tout à fait. Vous définissez le style d'expression de votre assistant (chaleureux, institutionnel, dynamique), le vouvoiement ou tutoiement, l'usage des emojis et les formules d'accueil selon l'identité de marque de votre entreprise.",
    },
    {
      q: "Faut-il changer de carte SIM ou avoir des compétences techniques ?",
      a: "Aucune compétence technique n'est requise et vous conservez votre carte SIM ainsi que votre numéro habituel. L'activation sécurisée se fait en moins de 2 minutes directement depuis votre smartphone. Aucun second appareil n'est nécessaire.",
    },
    {
      q: "Y a-t-il un engagement de durée ou des frais cachés ?",
      a: "Aucun engagement ni frais d'installation. Tous nos forfaits sont transparents et sans engagement. Vous pouvez faire évoluer votre formule ou résilier votre abonnement à tout moment en un clic depuis votre espace de gestion.",
    },
  ];



  return (
    <div className="w-full bg-white text-slate-900 antialiased min-h-screen">

      {/* ===== HEADER PUBLIC RÉUTILISABLE (Fixed, 100% transparent, masquage fluide) ===== */}
      <PublicHeader onNavigate={onNavigate} activeTab="landing" />

      <main id="main-content">
      {/* ===== HERO (Dégradé en déplacement & dispersion gravitationnelle au curseur) ===== */}
      <section id="hero" className="relative overflow-hidden bg-white text-slate-900 pt-[calc(5rem+env(safe-area-inset-top,0px))] sm:pt-28 md:pt-32 lg:pt-32 pb-12 sm:pb-16 md:pb-20">
        
        {/* Canvas de dégradé en mouvement et dispersion au curseur (sans grille/cadrans) */}
        <HeroGravityCanvas />

        <div className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-14 items-center">
            
            {/* Colonne Gauche : Contenu éditorial (aligné gauche sur PC, centré sur mobile) */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-4 sm:space-y-5">
              
              {/* Titre : direct, percutant et axé sur la valeur */}
              <h1 className="font-serif text-[28px] xs:text-[32px] sm:text-[42px] md:text-[48px] lg:text-[50px] xl:text-[54px] font-normal tracking-[-0.02em] leading-[1.15] text-slate-950">
                Un assistant IA qui répond à vos clients WhatsApp et prend vos commandes et rendez-vous.
              </h1>

              {/* Sous-titre : direct, percutant et sans bavardage */}
              <p className="text-sm sm:text-base md:text-[17px] text-slate-600 leading-relaxed font-normal max-w-xl mx-auto lg:mx-0">
                Ne laissez plus aucun client sans réponse. Zeyilo répond instantanément 24h/24, prend vos commandes et encaisse directement sur votre numéro WhatsApp.
              </p>

              {/* Boutons d'action minimalistes & compacts (sans surcharge) */}
              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-2.5 sm:gap-3">
                <button
                  onClick={() => { adPixels.track('Lead'); onNavigate('register' as ViewTab); }}
                  className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-slate-900 hover:bg-black text-white text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-sm cursor-pointer group"
                >
                  <span>Créer mon compte gratuit</span>
                  <span className="w-4 h-4 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center text-[10px] text-white font-bold transition-colors">↗</span>
                </button>
                <button
                  onClick={() => onNavigate('pricing')}
                  className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full bg-slate-100 hover:bg-slate-200/80 text-slate-800 text-xs sm:text-sm font-medium border border-slate-200/80 transition-all active:scale-95 cursor-pointer group"
                >
                  <span>Voir les tarifs</span>
                  <span className="text-slate-400 group-hover:text-slate-700 transition-colors">↗</span>
                </button>
              </div>

              {/* Mention discrète */}
              <p className="text-xs text-slate-500 font-normal">
                100% Gratuit pour démarrer · Sans carte bancaire · Accès immédiat
              </p>

              {/* Métriques épurées & horizontales */}
              <div className="pt-4 border-t border-slate-200/60 flex items-center justify-center lg:justify-start gap-6 sm:gap-8 text-slate-600 text-xs sm:text-sm">
                <div className="inline-flex items-center gap-2 font-medium text-slate-900">
                  <Zap className="w-4 h-4 text-slate-900 shrink-0" />
                  <span>~5s délai moyen</span>
                </div>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <div className="inline-flex items-center gap-2 font-medium text-slate-900">
                  <Clock className="w-4 h-4 text-slate-900 shrink-0" />
                  <span>24h/24 • 7j/7</span>
                </div>
              </div>

            </div>

            {/* Colonne Droite : Vitrine principale WhatsApp IA (affichée sur desktop, masquée sur mobile pour éliminer le grand espace vide) */}
            <div className="hidden lg:flex lg:col-span-5 items-center justify-end mt-6 sm:mt-8 lg:mt-0">
              <HeroPhoneShowcase />
            </div>

          </div>
        </div>
      </section>

      {/* ===== SECTION 1 • PRODUIT & CHAT IA (Texte Gauche / Mockup Droite) ===== */}
      <section id="chat-ia" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 border-t border-slate-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-16 items-center">
          
          {/* Texte Gauche */}
          <div className="lg:col-span-5 space-y-4 sm:space-y-5 text-center lg:text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-[11px] font-semibold tracking-wide">
              <span>01</span>
              <span>•</span>
              <span>Discussions & Automatisation</span>
            </div>

            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-[42px] font-normal tracking-[-0.02em] text-slate-950 leading-[1.18]">
              Des conversations WhatsApp qui vendent en temps réel.
            </h2>

            <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
              Vos clients posent leurs questions, demandent vos tarifs et passent commande directement dans leur messagerie. L'IA répond en 5 secondes en moyenne, comprend les vocaux et enregistre les coordonnées sans jamais faire attendre un prospect.
            </p>

            <div className="space-y-2.5 pt-1 text-left max-w-md mx-auto lg:mx-0">
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Comprend les messages écrits, vocaux et photos de produits.</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Synchronisation immédiate sur Web et Mobile avec notifications.</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Reprise en main humaine en un clic à tout instant.</span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => { adPixels.track('Lead'); onNavigate('register' as ViewTab); }}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-slate-900 hover:bg-black text-white text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-sm cursor-pointer group"
              >
                <span>Créer mon compte gratuit</span>
                <span className="w-4 h-4 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center text-[10px] text-white font-bold transition-colors">↗</span>
              </button>
            </div>
          </div>

          {/* Visuel Droite : Carte Bento Widget Chat IA (Fidèle au design de la maquette originale) */}
          <div className="lg:col-span-7 flex items-center justify-center lg:justify-end">
            <div className="w-full max-w-[460px] relative rounded-3xl bg-[#FDE789] p-4 sm:p-6 shadow-lg shadow-amber-500/10 space-y-3">
              
              {/* En-tête : Miniatures d'articles en capsules rondes */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-white/90 p-0.5 shadow-xs overflow-hidden flex items-center justify-center shrink-0">
                  <img src="/scenes/boutique-commande-wax.webp" alt="Robe Wax" className="w-full h-full object-cover rounded-full" />
                </div>
                <div className="w-8 h-8 rounded-full bg-white/90 p-0.5 shadow-xs overflow-hidden flex items-center justify-center shrink-0">
                  <img src="/scenes/restaurateur-succes.webp" alt="Menu" className="w-full h-full object-cover rounded-full" />
                </div>
                <div className="w-8 h-8 rounded-full bg-white/90 shadow-xs flex items-center justify-center text-xs font-bold text-amber-900 shrink-0">
                  +12
                </div>
                <span className="text-[11px] font-semibold text-amber-950/70 ml-1">
                  Catalogue & stocks en direct
                </span>
              </div>

              {/* Bulle 1 (Gauche) : Message du client avec vocal */}
              <div className="bg-white rounded-2xl rounded-tl-sm p-3.5 sm:p-4 shadow-xs space-y-2.5 max-w-[92%]">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs sm:text-[13px] font-medium text-slate-800 leading-relaxed">
                    « Bonjour ! Avez-vous la robe wax bleue en taille M disponible aujourd'hui pour Cocody ? »
                  </p>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 mt-1" />
                </div>

                {/* Simulation Note Vocale */}
                <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between gap-3 text-slate-400">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600">
                    <Mic className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Vocal transcrit</span>
                    <span className="font-mono text-[10px] text-slate-400">0:14</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <span className="w-0.5 h-2.5 bg-slate-300 rounded-full" />
                    <span className="w-0.5 h-4 bg-emerald-500 rounded-full" />
                    <span className="w-0.5 h-2 bg-slate-300 rounded-full" />
                    <span className="w-0.5 h-4.5 bg-emerald-500 rounded-full" />
                    <span className="w-0.5 h-2.5 bg-slate-300 rounded-full" />
                  </div>
                </div>
              </div>

              {/* Bulle 2 (De l'autre côté / Droite) : Réponse de l'IA Zeyilo */}
              <div className="ml-auto bg-slate-950 text-white rounded-2xl rounded-tr-sm p-3.5 sm:p-4 shadow-md space-y-1.5 max-w-[92%]">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
                    <span className="text-[10px] font-bold text-amber-300 uppercase tracking-wider">Réponse IA Zeyilo</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono">~5s</span>
                </div>

                <p className="text-xs sm:text-[13px] text-slate-100 leading-relaxed font-normal">
                  « Oui, elle est disponible en stock en taille M (18 500 FCFA) ! 👗 Nous livrons aujourd'hui à Cocody sous 2h. Souhaitez-vous valider votre commande ? »
                </p>

                <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/10 text-[10px] text-slate-400">
                  <div className="flex items-center gap-1 text-emerald-400">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                    <span>Envoyé automatiquement</span>
                  </div>
                  <span className="text-[10px] font-bold text-slate-300 bg-white/10 px-2 py-0.5 rounded-full">
                    1 clic • Wave / OM
                  </span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ===== SECTION 2 • E-COMMERCE & ENCAISSEMENT (Visuel Gauche / Texte Droite) ===== */}
      <section id="boutique-ecommerce" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 border-t border-slate-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-16 items-center">
          
          {/* Visuel Gauche : Story format épuré avec carte Bento UI Commande reçue (Fidèle au design de la maquette) */}
          <div className="lg:col-span-6 order-2 lg:order-1 flex items-center justify-center lg:justify-start">
            <div className="relative">
              {/* Image principale arrondie */}
              <div className="w-[240px] xs:w-[260px] sm:w-[290px] md:w-[310px] aspect-[3/4] rounded-3xl overflow-hidden shadow-xl bg-slate-900 relative">
                <img
                  src="/scenes/entrepreneure-boutique-wax.webp"
                  alt="Entrepreneure recevant une commande automatisée dans sa boutique"
                  width={576}
                  height={1024}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Carte Bento UI : Commande reçue (Superposée, débute au centre de l'image et déborde sur le côté droit vers le texte) */}
              <div className="absolute -bottom-4 sm:-bottom-5 left-[22%] sm:left-[28%] -right-4 sm:-right-8 bg-white/95 backdrop-blur-md rounded-2xl p-3 sm:p-3.5 shadow-2xl border border-slate-100 z-20">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                    <span className="text-[11px] font-bold text-slate-900">Commande reçue</span>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 shrink-0">
                    Payé Wave
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-slate-950 truncate leading-tight">Robe Wax Ankara</h4>
                    <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium">Taille M • Cocody</span>
                  </div>
                  <span className="text-xs sm:text-sm font-extrabold text-slate-950 shrink-0">18 500 FCFA</span>
                </div>
              </div>
            </div>
          </div>

          {/* Texte Droite */}
          <div className="lg:col-span-6 order-1 lg:order-2 space-y-4 sm:space-y-5 text-center lg:text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-[11px] font-semibold tracking-wide">
              <span>02</span>
              <span>•</span>
              <span>Catalogue & Commandes</span>
            </div>

            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-[42px] font-normal tracking-[-0.02em] text-slate-950 leading-[1.18]">
              Votre boutique ouverte 24h/24, même quand vous dormez.
            </h2>

            <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
              Fini de répéter 50 fois par jour vos prix et vos disponibilités. Vos clients parcourent votre mini-catalogue interactif, choisissent leurs options et transmettent leur commande en toute autonomie.
            </p>

            <div className="space-y-2.5 pt-1 text-left max-w-md mx-auto lg:mx-0">
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Paiements directs Wave, Orange Money, MTN et Moov sans commission.</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Validation humaine des preuves de paiement avant expédition.</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Relance polie et automatique des paniers non finalisés.</span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => { adPixels.track('Lead'); onNavigate('register' as ViewTab); }}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-slate-900 hover:bg-black text-white text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-sm cursor-pointer group"
              >
                <span>Découvrir l'offre gratuite</span>
                <span className="w-4 h-4 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center text-[10px] text-white font-bold transition-colors">↗</span>
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* ===== SECTION 3 • RELAIS HUMAIN & CONTRÔLE TOTAL (Texte Gauche / Portrait Droite) ===== */}
      <section id="relais-humain" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 border-t border-slate-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-16 items-center">
          
          {/* Colonne Gauche : Titre & Explications */}
          <div className="lg:col-span-6 space-y-6 sm:space-y-8 text-center lg:text-left">
            <div className="space-y-3 sm:space-y-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-[11px] font-semibold tracking-wide">
                <span>03</span>
                <span>•</span>
                <span>Relais Humain & Contrôle Total</span>
              </div>

              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-[42px] font-normal tracking-[-0.02em] text-slate-950 leading-[1.18]">
                L'IA qualifie vos prospects. <br className="hidden sm:inline" />
                <span className="font-serif italic font-normal text-slate-900">Vous reprenez la main en 1 clic.</span>
              </h2>

              <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
                Zeyilo ne remplace pas votre relation humaine : elle la sublime. Vos conseillers ou vous-même intervenez à tout instant dans la discussion. L'IA s'efface instantanément dès que vous écrivez.
              </p>
            </div>

            <div className="space-y-3 pt-1 text-left">
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Notification push instantanée lorsqu'un client demande un conseiller humain.</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Mise en pause automatique et silencieuse de l'IA dès que vous répondez au client.</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Historique complet et contexte résumé pour finaliser la commande sans friction.</span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => { adPixels.track('Lead'); onNavigate('register' as ViewTab); }}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-slate-900 hover:bg-black text-white text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-sm cursor-pointer group"
              >
                <span>Activer le mode hybride IA + Humain</span>
                <span className="w-4 h-4 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center text-[10px] text-white font-bold transition-colors">↗</span>
              </button>
            </div>
          </div>

          {/* Colonne Droite : Visuel Conseiller Humain & Smartphone (Fidèle à la photo fournie) */}
          <div className="lg:col-span-6 flex items-center justify-center lg:justify-end">
            <div className="relative">
              {/* Image principale arrondie */}
              <div className="w-[240px] xs:w-[260px] sm:w-[290px] md:w-[310px] aspect-[3/4] rounded-3xl overflow-hidden shadow-xl bg-slate-900 relative">
                <img
                  src="/scenes/relais-conseiller-humain.webp"
                  alt="Conseiller humain prenant le relais de l'IA sur WhatsApp"
                  width={576}
                  height={1024}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Carte Bento UI : Mode Hybride (Superposée, débute au centre et déborde à gauche vers le texte) */}
              <div className="absolute -bottom-4 sm:-bottom-5 -left-4 sm:-left-8 right-[22%] sm:right-[28%] bg-white/95 backdrop-blur-md rounded-2xl p-3 sm:p-3.5 shadow-2xl border border-slate-100 z-20">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                    <span className="text-[11px] font-bold text-slate-900 truncate">Conseiller humain</span>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 shrink-0">
                    Disponible
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                  <div>
                    <h4 className="text-xs font-bold text-slate-950 leading-tight">Prise en main en 1 clic</h4>
                    <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium">L'IA s'efface automatiquement</span>
                  </div>
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg shrink-0">
                    0 coupure
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ===== SECTION 4 • MÉTRIQUES & PREUVE SOCIALE (Visuel Gauche / Texte Droite) ===== */}
      <section id="metriques-impact" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 border-t border-slate-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-16 items-center">
          
          {/* Colonne Gauche : Portrait Commerçant & Discussions avec Carte Bento UI */}
          <div className="lg:col-span-6 order-2 lg:order-1 flex items-center justify-center lg:justify-start">
            <div className="relative">
              {/* Image principale arrondie */}
              <div className="w-[240px] xs:w-[260px] sm:w-[290px] md:w-[310px] aspect-[3/4] rounded-3xl overflow-hidden shadow-xl bg-slate-900 relative">
                <img
                  src="/scenes/commercant-discussions.webp"
                  alt="Commerçant avec son smartphone gérant ses discussions et commandes WhatsApp"
                  width={576}
                  height={1024}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Carte Bento UI : Performance WhatsApp (Superposée, débute au centre et déborde à droite vers le texte) */}
              <div className="absolute -bottom-4 sm:-bottom-5 left-[22%] sm:left-[28%] -right-4 sm:-right-8 bg-white/95 backdrop-blur-md rounded-2xl p-3 sm:p-3.5 shadow-2xl border border-slate-100 z-20">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                      <TrendingUp className="w-2.5 h-2.5 stroke-[2.5]" />
                    </div>
                    <span className="text-[11px] font-bold text-slate-900 truncate">Performance WhatsApp</span>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 shrink-0">
                    En direct
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                  <div>
                    <span className="text-sm sm:text-base font-bold text-slate-950 block leading-tight">+70%</span>
                    <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium">Ventes converties</span>
                  </div>
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg shrink-0">
                    Disponible 24h/24
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne Droite : Titre & Métriques Chiffrées */}
          <div className="lg:col-span-6 order-1 lg:order-2 space-y-6 sm:space-y-8 text-center lg:text-left">
            <div className="space-y-3 sm:space-y-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-[11px] font-semibold tracking-wide">
                <span>04</span>
                <span>•</span>
                <span>Impact Concret & Performances</span>
              </div>

              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-[42px] font-normal tracking-[-0.02em] text-slate-950 leading-[1.18]">
                Des performances prouvées pour le commerce africain.
              </h2>

              <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
                Chaque minute d'attente sur WhatsApp divise vos chances de conversion par deux. Zeyilo supprime la lenteur et fidélise vos clients dès le premier échange.
              </p>
            </div>

            {/* 3 Métriques Minimalistes Bento */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-left">
              <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 border-0 shadow-none space-y-1">
                <span className="font-serif text-2xl sm:text-3xl font-bold text-slate-950">~5s</span>
                <p className="text-xs text-slate-600 font-medium leading-snug">Délai moyen de réponse 24h/24</p>
              </div>

              <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 border-0 shadow-none space-y-1">
                <span className="font-serif text-2xl sm:text-3xl font-bold text-slate-950">+65%</span>
                <p className="text-xs text-slate-600 font-medium leading-snug">De conversion des demandes d'achat</p>
              </div>

              <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 border-0 shadow-none space-y-1">
                <span className="font-serif text-2xl sm:text-3xl font-bold text-slate-950">0 client</span>
                <p className="text-xs text-slate-600 font-medium leading-snug">Perdu pendant vos heures de rush</p>
              </div>
            </div>

            <div className="pt-1">
              <button
                onClick={() => { adPixels.track('Lead'); onNavigate('register' as ViewTab); }}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-slate-900 hover:bg-black text-white text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-sm cursor-pointer group"
              >
                <span>Rejoindre les commerçants actifs</span>
                <span className="w-4 h-4 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center text-[10px] text-white font-bold transition-colors">↗</span>
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* ===== SECTION 4 • LE BLOG & STRATÉGIES (3 Cartes Épurées) ===== */}
      <section id="blog-apercu" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 border-t border-slate-100">
        <div className="max-w-3xl mx-auto text-center space-y-3 mb-10 sm:mb-14">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest block">
            • Notre Blog & Stratégies •
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-normal tracking-[-0.02em] text-slate-950">
            Les derniers conseils pour vendre sur WhatsApp
          </h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto">
            Guides pratiques, études de cas et méthodes éprouvées pour développer votre commerce en Afrique francophone.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-6">
          {BLOG_POSTS.slice(0, 3).map((post) => (
            <a
              key={post.slug}
              href={`/blog/${post.slug}`}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(`blog/${post.slug}` as any);
              }}
              className="group flex flex-row sm:flex-col items-center sm:items-stretch gap-3.5 sm:gap-0 bg-slate-50/70 hover:bg-slate-100/90 rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-5 transition-all duration-300 border-0"
            >
              {/* Image compacte : vignette sur mobile, pleine largeur sur sm+ */}
              <div className="w-20 h-20 sm:w-full sm:h-auto sm:aspect-[16/10] rounded-xl sm:rounded-2xl overflow-hidden bg-slate-200 shrink-0 sm:mb-4 relative">
                <img
                  src={post.coverImage || (post as any).image}
                  alt={post.coverImageAlt || post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Contenu textuel épuré sans description lourde */}
              <div className="min-w-0 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] sm:text-[11px] text-slate-500 mb-1 sm:mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-white text-slate-700 font-semibold shadow-2xs">
                      {post.category}
                    </span>
                    <span>•</span>
                    <span>{post.readTime} min</span>
                  </div>
                  <h3 className="font-serif text-xs sm:text-base lg:text-lg font-normal text-slate-900 group-hover:text-black line-clamp-2 leading-snug">
                    {post.title}
                  </h3>
                </div>

                <div className="flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-slate-800 pt-1.5 sm:pt-3 sm:mt-2 sm:border-t sm:border-slate-200/50 group-hover:text-black group-hover:gap-2 transition-all">
                  <span>Lire l'article</span>
                  <span className="text-slate-400 group-hover:text-slate-900 transition-colors">↗</span>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 sm:mt-12 text-center">
          <a
            href="/blog"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('blog' as any);
            }}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 hover:bg-black text-white text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-xs cursor-pointer group"
          >
            <span>Voir tous les articles du blog</span>
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </section>

      {/* ===== SECTION • PROFILS D'ACTIVITÉS AUTOMATISÉS (Au fond, avant le CTA) ===== */}
      <section id="profils-activites" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl lg:rounded-[36px] bg-gradient-to-br from-slate-950 via-slate-900 to-black p-6 sm:p-10 md:p-12 lg:p-14 shadow-2xl">
          
          <div className="relative z-10 max-w-3xl mx-auto text-center space-y-3 mb-8 sm:mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-normal tracking-[-0.02em] text-white leading-tight">
              Profils d'activités automatisés
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-slate-300 max-w-2xl mx-auto font-normal leading-relaxed">
              Zeyilo adapte son vocabulaire, ses règles de commande et ses formulaires selon votre secteur.
            </p>
          </div>

          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-6 gap-3.5 sm:gap-4.5">
            {/* Profil 1: Boutique & Vente */}
            <div className="sm:col-span-3 lg:col-span-2 p-5 sm:p-6 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] transition-all space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
                <ShoppingBag className="w-5 h-5 stroke-[2.2]" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-sm sm:text-base font-bold text-white leading-tight">Boutique & Vente</h3>
                <p className="text-xs sm:text-[13px] text-slate-300 font-normal leading-relaxed">
                  Articles, variantes de taille et de couleur, panier, frais de livraison et preuve de paiement Wave ou Orange Money.
                </p>
              </div>
            </div>

            {/* Profil 2: Rendez-vous & Salons */}
            <div className="sm:col-span-3 lg:col-span-2 p-5 sm:p-6 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] transition-all space-y-3">
              <div className="w-10 h-10 rounded-xl bg-teal-500/15 text-teal-300 flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 stroke-[2.2]" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-sm sm:text-base font-bold text-white leading-tight">Rendez-vous & Salons</h3>
                <p className="text-xs sm:text-[13px] text-slate-300 font-normal leading-relaxed">
                  Instituts de beauté, salons de coiffure, cabinets : sélection du créneau horaire, confirmation et rappels automatiques.
                </p>
              </div>
            </div>

            {/* Profil 3: Réservations & Hôtels */}
            <div className="sm:col-span-6 lg:col-span-2 p-5 sm:p-6 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] transition-all space-y-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/15 text-purple-300 flex items-center justify-center shrink-0">
                <CalendarCheck className="w-5 h-5 stroke-[2.2]" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-sm sm:text-base font-bold text-white leading-tight">Réservations & Hôtels</h3>
                <p className="text-xs sm:text-[13px] text-slate-300 font-normal leading-relaxed">
                  Résidences meublées, tables, chambres : dates de séjour, disponibilités en temps réel et versement d'acompte.
                </p>
              </div>
            </div>

            {/* Profil 4: Artisans & Devis */}
            <div className="sm:col-span-3 lg:col-span-3 p-5 sm:p-6 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] transition-all space-y-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-300 flex items-center justify-center shrink-0">
                <Briefcase className="w-5 h-5 stroke-[2.2]" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-sm sm:text-base font-bold text-white leading-tight">Artisans & Devis</h3>
                <p className="text-xs sm:text-[13px] text-slate-300 font-normal leading-relaxed">
                  Plomberie, climatisation, rénovation : recueil précis du besoin client, réception des photos du problème et envoi de pré-devis.
                </p>
              </div>
            </div>

            {/* Profil 5: Support Client & SAV */}
            <div className="sm:col-span-3 lg:col-span-3 p-5 sm:p-6 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] transition-all space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500/15 text-sky-300 flex items-center justify-center shrink-0">
                <Headphones className="w-5 h-5 stroke-[2.2]" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-sm sm:text-base font-bold text-white leading-tight">Support Client & SAV</h3>
                <p className="text-xs sm:text-[13px] text-slate-300 font-normal leading-relaxed">
                  Suivi de commande et d'expédition, gestion des réclamations, horaires et localisation : assistance 24h/24 sans attente.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ===== SECTION 5 • CTA FINAL (Style Chrome Waves Moderne & Épuré) ===== */}
      <section id="cta-banner" className="max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl lg:rounded-[36px] bg-black border border-white/10 p-7 sm:p-10 md:p-12 lg:p-14 shadow-2xl">
          
          {/* Image de fond vagues métalliques avec fondu sur la gauche */}
          <div 
            className="absolute inset-0 bg-cover bg-right sm:bg-center opacity-85 pointer-events-none"
            style={{ backgroundImage: `url('/cta-waves-bg.webp')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-transparent pointer-events-none" />
          
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 sm:gap-8">
            
            {/* Titre à gauche */}
            <div className="max-w-xl space-y-2 text-left">
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-normal tracking-[-0.02em] text-white leading-tight">
                Prêt à accélérer vos ventes et conversions ?
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 font-normal">
                Jumelez votre WhatsApp en 2 minutes. 50 réponses IA offertes, sans engagement ni carte bancaire.
              </p>
            </div>

            {/* Boutons d'action à droite */}
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => onNavigate('pricing')}
                className="flex-1 md:flex-initial px-5 py-2.5 rounded-full text-xs sm:text-sm font-medium bg-white/10 hover:bg-white/15 text-white border border-white/20 backdrop-blur-md transition-all active:scale-95 cursor-pointer text-center"
              >
                Voir les forfaits
              </button>
              <button
                onClick={() => { adPixels.track('Lead'); onNavigate('register' as ViewTab); }}
                className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white hover:bg-slate-100 text-slate-950 font-semibold text-xs sm:text-sm transition-all active:scale-95 cursor-pointer shadow-sm group"
              >
                <span>Tester gratuitement</span>
                <span className="w-3.5 h-3.5 rounded-full bg-slate-950/15 flex items-center justify-center text-[9px] text-slate-950 font-bold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform">↗</span>
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 space-y-8">
        <div className="text-center space-y-2">
          <h2 className="font-serif text-2xl sm:text-3xl font-normal tracking-[-0.02em] text-slate-950">
            Questions fréquentes
          </h2>
          <p className="text-sm text-slate-600">
            Tout ce que vous devez savoir pour automatiser vos ventes et votre support WhatsApp.
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200/80 divide-y divide-slate-100 overflow-hidden shadow-2xs">
          {(showAllFaqs ? faqs : faqs.slice(0, 3)).map((faq, i) => (
            <div key={i} className="p-5 sm:p-6 transition-colors hover:bg-slate-50/40">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between text-left gap-4 font-bold text-sm text-slate-900 cursor-pointer select-none group"
              >
                <span className="group-hover:text-slate-950 transition-colors">{faq.q}</span>
                <ChevronRight
                  className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${
                    openFaq === i ? 'rotate-90 text-slate-900' : 'group-hover:text-slate-700'
                  }`}
                />
              </button>
              {openFaq === i && (
                <div className="overflow-hidden animate-in fade-in duration-150">
                  <p className="pt-3 text-sm text-slate-600 leading-relaxed font-normal">
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bouton Voir plus / Voir moins */}
        {faqs.length > 3 && (
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => setShowAllFaqs(!showAllFaqs)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200/80 active:scale-95 text-slate-800 text-xs sm:text-sm font-semibold transition-all cursor-pointer border border-slate-200/60"
            >
              <span>{showAllFaqs ? 'Voir moins de questions' : `Voir plus de questions (${faqs.length})`}</span>
              <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform duration-200 ${showAllFaqs ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}

        {/* Support & Contact Card - Design Bento épuré aligné sur l'onglet Relances de l'Agent IA */}
        <div className="bg-slate-100/80 rounded-2xl sm:rounded-3xl p-5 sm:p-6 border-0 shadow-none space-y-4 sm:space-y-5">
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white text-slate-900 flex items-center justify-center shrink-0 border-0 shadow-none mt-0.5">
              <Headphones className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                Une autre question ou besoin d'un accompagnement ?
              </h3>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mt-1">
                Notre équipe est disponible 7j/7 pour vous guider, répondre à vos questions et vous assister dans la configuration de votre assistant.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2.5 sm:gap-3">
            <a
              href={`https://wa.me/2290129647182?text=${encodeURIComponent("Bonjour l'équipe Zeyilo, j'ai une question concernant l'assistant WhatsApp :")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-black active:scale-[0.98] text-white font-medium text-xs sm:text-sm shadow-xs transition-all cursor-pointer"
            >
              <WhatsAppIcon variant="transparent" size={16} className="text-white" />
              <span>Contacter sur WhatsApp</span>
            </a>

            <a
              href={`mailto:support@zeyilo.com?subject=${encodeURIComponent("[Zeyilo] Assistance client")}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-800 font-medium text-xs sm:text-sm shadow-xs transition-all cursor-pointer border border-slate-200/80"
            >
              <Mail className="w-4 h-4 text-slate-600 stroke-[2.2]" />
              <span>support@zeyilo.com • 7j/7</span>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-2.5 pt-1">
            <div className="bg-white rounded-xl p-3 sm:p-3.5 border-0 shadow-none flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-slate-950 text-white shadow-2xs">
                <Check className="w-3 h-3 stroke-[3]" />
              </div>
              <span className="text-xs font-semibold text-slate-800 leading-snug">
                Réponse immédiate sous 1 min
              </span>
            </div>

            <div className="bg-white rounded-xl p-3 sm:p-3.5 border-0 shadow-none flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-slate-950 text-white shadow-2xs">
                <Check className="w-3 h-3 stroke-[3]" />
              </div>
              <span className="text-xs font-semibold text-slate-800 leading-snug">
                Support technique 7j/7
              </span>
            </div>

            <div className="bg-white rounded-xl p-3 sm:p-3.5 border-0 shadow-none flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-slate-950 text-white shadow-2xs">
                <Check className="w-3 h-3 stroke-[3]" />
              </div>
              <span className="text-xs font-semibold text-slate-800 leading-snug">
                Accompagnement personnalisé offert
              </span>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* ===== FOOTER ===== */}
      <footer id="footer" className="border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2.5">
            <img src="/logo.webp" alt="Zeyilo" width={320} height={189} loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
            <span>— L'Agent IA WhatsApp des entreprises modernes</span>
          </div>
          <nav aria-label="Navigation pied de page" className="flex flex-wrap items-center justify-center gap-4 sm:gap-5">
            <a
              href="/register"
              onClick={(e) => {
                e.preventDefault();
                onNavigate('register' as ViewTab);
              }}
              className="hover:text-slate-900 font-medium transition-colors"
            >
              Inscription
            </a>
            <a
              href="/pricing"
              onClick={(e) => {
                e.preventDefault();
                onNavigate('pricing');
              }}
              className="hover:text-slate-900 font-medium transition-colors"
            >
              Tarifs
            </a>
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                onNavigate('login' as ViewTab);
              }}
              className="hover:text-slate-900 font-medium transition-colors"
            >
              Connexion
            </a>
            <a href="/partenariat" className="hover:text-slate-900 transition-colors">
              Partenariat
            </a>
            <a href="/blog" className="hover:text-slate-900 transition-colors">
              Blog
            </a>
            <a href="/confidentialite" className="hover:text-slate-900 transition-colors">
              Confidentialité
            </a>
            <a href="/cgu" className="hover:text-slate-900 transition-colors">
              Conditions Générales
            </a>
          </nav>
        </div>
      </footer>

    </div>
  );
};
